import { type VFC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { Tag } from "widgets";
import { toSafeInt } from "widgets/utils";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntityGenomeView from "../genome-viewer/GenericEntityGenomeView.js";
import { type IGenomeViewOwnProps } from "../genome-viewer/GenomeView.js";
import { renderTranscriptAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { getGeneTypeColor } from "@/app-utils/color.ts";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { getPaddedRange } from "@/app-utils/range.ts";
import { type Region, type TranscriptLocation } from "@/graphql.ts";
import { getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { type ITranscriptState, shouldLoadTranscript } from "@/redux/modules/transcript.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { SEQUENCE_TYPE_LABEL, TRACK_TYPE } from "@/settings/settings.ts";

const TranscriptGenomeView: VFC = () => {
  const { transcriptId } = useParams<"transcriptId">();
  const { transcript } = useSelector<IAppState, ITranscriptState>((state) => state.transcript);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadTranscript, transcriptId);

  useEffect(() => {
    void load();
  }, [load]);

  let genomeViewProps: IGenomeViewOwnProps;

  if (transcript) {
    const locations: TranscriptLocation[] = transcript.transcriptLocations;
    const region: Region =
      locations.length > 0 && locations[0].transcript && locations[0].transcript.id
        ? locations.find((t) => Number.parseInt(t.transcript.id) === toSafeInt(transcriptId)).region
        : locations[0].region;
    const sequenceTypeLabel = transcript && SEQUENCE_TYPE_LABEL[transcript.sequenceTypeId];
    const tag = transcript && sequenceTypeLabel && (
      <Tag label={sequenceTypeLabel} intentColor={getGeneTypeColor(sequenceTypeLabel)} />
    );
    genomeViewProps = {
      genomeId: transcript.genome.id,
      contigId: region.contig.id,
      range: getPaddedRange(region.range, region.contig.length),
      defaultTracks: getClassicGenomeBrowserTracks([
        TRACK_TYPE.SEQUENCE,
        TRACK_TYPE.GENE,
        TRACK_TYPE.TRANSCRIPT_LOCATION,
      ]),
      headerTags: [tag],
      genomes: [transcript.genome],
      attributesWidgets: [
        <AttributesListWidget
          attributes={renderTranscriptAttributesWidgets(transcript)}
          key={"transcript-genome-view-widget-1"}
        />,
      ],
      title: transcript?.sequenceId,
      subtitle: transcript?.description || "Genome View",
    };
  }

  return <GenericEntityGenomeView genomeViewProps={genomeViewProps} loadEntityData={() => void load()} />;
};

export default TranscriptGenomeView;
